import { useCallback } from 'react';
import { DocumentWorkflowTemplateInfo } from '@dealstack/shared/lib/generated';
import { useSetPostDating } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useSetPostDating.ts';
import { useDeleteMasterDocument } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useDeleteMasterDocument.ts';
import { useArchiveMasterDocument } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useArchiveMasterDocument.ts';
import { useClassifierOptions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useClassifierOptions.ts';
import {
	MASTER_DOCUMENT_FORM_FIELD,
	MasterDocumentFormDto,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/MasterDocumentForm/types.ts';
import { useMasterDocumentSettingsForm } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/shared/useMasterDocumentSettingsForm';

interface Props {
	editContract: DocumentWorkflowTemplateInfo;
	onDeleteSuccess?: () => void;
}

export const useEditMasterDocuments = ({ editContract, onDeleteSuccess }: Props) => {
	const { mutateAsync: setPostDatingAsync, isPending: isSettingPostDating } = useSetPostDating();
	const { mutateAsync: deleteAsync, isPending: isDeleting } =
		useDeleteMasterDocument(onDeleteSuccess);
	const { mutateAsync: archiveAsync, isPending: isArchiving } =
		useArchiveMasterDocument(onDeleteSuccess);

	const classifierOptions = useClassifierOptions();

	const { form, save, isSaving } = useMasterDocumentSettingsForm(editContract);

	// values already reflect current form state — save() re-reads it via form.handleSubmit.
	const onSubmit = useCallback(
		async (_values: MasterDocumentFormDto) => {
			await save();
		},
		[save]
	);

	/**
	 * Saved on toggle, not on Save; the form value is reverted if the write fails. Never dirties
	 * the form, since the value is already persisted.
	 */
	const onTogglePostDating = useCallback(
		async (next: boolean) => {
			const templateId = editContract?.id;
			if (!templateId) {
				return;
			}
			form.setValue(MASTER_DOCUMENT_FORM_FIELD.requiresPostDating, next, {
				shouldDirty: false,
			});
			try {
				await setPostDatingAsync({ templateId, requiresPostDating: next });
			} catch {
				// The mutation's own toast is the report.
				form.setValue(MASTER_DOCUMENT_FORM_FIELD.requiresPostDating, !next, {
					shouldDirty: false,
				});
			}
		},
		[editContract?.id, form, setPostDatingAsync]
	);

	const onArchive = useCallback(async () => {
		const templateId = editContract?.id;
		const isCurrentlyArchived = editContract?.archived ?? false;
		await archiveAsync({
			templateId,
			archived: !isCurrentlyArchived,
		});
	}, [archiveAsync, editContract?.id, editContract?.archived]);

	const onDelete = useCallback(async () => {
		const templateId = editContract?.id;
		await deleteAsync({ templateId });
	}, [deleteAsync, editContract?.id]);

	return {
		form,
		classifierOptions,
		onSubmit,
		onTogglePostDating,
		isSettingPostDating,
		onArchive,
		onDelete,
		isUpdating: isSaving,
		isArchiving,
		isDeleting,
	};
};
